import { BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath, useReactFlow, useNodes } from 'reactflow';
import React, { useCallback, useMemo } from 'react';
import { RemoveElementBtn } from '../common/RemoveElementBtn';
import { useParams } from 'react-router';
import { MergeConfiguration, PythonFunctionConfiguration, WorkflowEdge, WorkflowNode } from '../../../../api/model.ts';
import { useTheme } from '@mui/material';
import { EdgeIndexBadge, EdgeLabelChip } from './CustomEdge.styles';

type MergeEdgeOrder = {
  index: number;
  column: string;
};

export interface CustomEdgeData {
  isReconnecting?: boolean;
}

export default function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
}: EdgeProps<CustomEdgeData>) {
  const reactFlow = useReactFlow<WorkflowNode, WorkflowEdge>();
  const { workflowRevision } = useParams();
  const theme = useTheme();
  const nodes = useNodes<WorkflowNode>();
  const [strokeColor, setStrokeColor] = React.useState('');

  const edgeInstance = reactFlow?.getEdge(id);
  const targetNodeData = nodes.find((n) => n.id === edgeInstance?.target)?.data;

  const isRemovable = useMemo(() => {
    if (workflowRevision) {
      return false;
    }
    if (edgeInstance?.deletable === false) {
      return false;
    }
    return true;
  }, [workflowRevision, edgeInstance]);

  const mergeOrder: MergeEdgeOrder | null = useMemo(() => {
    // display order for edges targeting merge nodes
    if (!edgeInstance) {
      return null;
    }
    const configuration = targetNodeData?.configuration as MergeConfiguration;
    if (!configuration?.join_order) {
      return null;
    }
    const index = configuration.join_order.findIndex((c) => c.node_id === edgeInstance.source);
    if (index === -1) {
      return null;
    }
    return {
      index,
      column: configuration.join_order[index].column,
    };
  }, [edgeInstance, targetNodeData]);

  React.useEffect(() => {
    if (data?.isReconnecting) {
      const interval = setInterval(() => {
        setStrokeColor((prev) => (prev === 'black' ? '' : 'black'));
      }, 250);
      return () => clearInterval(interval);
    } else if (mergeOrder && !mergeOrder.column) {
      setStrokeColor(theme.palette.error.light);
    } else {
      setStrokeColor('');
    }
  }, [data?.isReconnecting, mergeOrder, mergeOrder?.column]);

  const pythonFunctionOrder: number | null = useMemo(() => {
    // display order for edges targeting python function nodes
    if (!edgeInstance) {
      return null;
    }
    const configuration = targetNodeData?.configuration as PythonFunctionConfiguration;
    if (!configuration?.node_order) {
      return null;
    }
    const index = configuration.node_order.indexOf(edgeInstance.source);
    if (index === -1) {
      return null;
    }
    return index;
  }, [edgeInstance, targetNodeData]);

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const onEdgeClick = useCallback(() => {
    reactFlow.setEdges((edges) => edges.filter((e) => e.id != id));
  }, [id, reactFlow]);

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke: strokeColor,
        }}
      />
      <EdgeLabelRenderer>
        <>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              fontSize: 12,
              pointerEvents: 'all',
            }}
            className="nodrag nopan"
          >
            {isRemovable && <RemoveElementBtn onClick={onEdgeClick} />}
          </div>
          {mergeOrder && (
            <EdgeLabelChip
              error={!mergeOrder.column}
              style={{ transform: `translate(-50%, -50%) translate(${labelX}px,${labelY - 30}px)` }}
            >
              <EdgeIndexBadge error={!mergeOrder.column}>{mergeOrder.index + 1}</EdgeIndexBadge>
              {mergeOrder.column || 'column not set'}
            </EdgeLabelChip>
          )}
          {pythonFunctionOrder !== null && (
            <EdgeLabelChip style={{ transform: `translate(-50%, -50%) translate(${labelX}px,${labelY - 30}px)` }}>
              input_df_{pythonFunctionOrder + 1}
            </EdgeLabelChip>
          )}
        </>
      </EdgeLabelRenderer>
    </>
  );
}
